import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Group, Loader, ScrollArea, Stack, Text, Title } from "@mantine/core";
import { memo, useEffect, useRef, useState } from "react";
import { useStyleEditor } from "../../components/StyleEditorContext.js";
import { useIntl } from "react-intl";
import { useChatId } from "../../hooks/assistant.js";
import { useChatStore } from "../../hooks/useChatStore.js";
import { getInitStyle } from "../../reducers/initReducer.js";
import { useResizeObserver } from "../../hooks/resize.js";
import { useAppSelector } from "../../hooks/redux.js";
import { AssistantBlock } from "./AssistantMessage.js";
import { UserMessage } from "./UserMessage.js";
import { SystemMessage } from "./SystemMessage.js";
import { ChatMessagesHeader } from "./ChatMessagesHeader.js";
import { ChatSuggestions } from "./ChatSuggestions.js";
import { useChatStoreState } from "../../hooks/useChatStoreState.js";
import { last } from "es-toolkit/compat";
const ChatEmptyState = ({ inputRef }) => {
    const intl = useIntl();
    return (_jsxs(Stack, { flex: 1, align: "center", justify: "center", gap: "lg", children: [_jsxs(Stack, { align: "center", gap: "md", ta: "center", children: [_jsx(Title, { order: 3, children: intl.formatMessage({ id: "chat.emptyTitle", defaultMessage: "Start creating your map with AI" }) }), _jsx(Text, { size: "sm", maw: 289, children: intl.formatMessage({ id: "chat.emptySubtitle", defaultMessage: "You can ask AI to style or modify your map using natural language." }) })] }), _jsx(ChatSuggestions, { inputRef: inputRef })] }));
};
const useFirstRenderRef = () => {
    const firstRender = useRef(true);
    useEffect(() => {
        firstRender.current = false;
    }, []);
    return firstRender;
};
export const ChatMessages = memo(({ inputWrapperRef, inputRef }) => {
    const [inputHeight, setHeight] = useState(0);
    const viewportRef = useRef(null);
    const firstRender = useFirstRenderRef();
    const chatId = useChatId();
    const intl = useIntl();
    const style = useAppSelector(getInitStyle);
    const { initialized, connectionState } = useChatStoreState(style.id);
    const { messages } = useChatStore(style.id, chatId);
    const { on, off } = useStyleEditor();
    useEffect(() => {
        last(viewportRef.current?.querySelectorAll("[data-list-item]"))?.scrollIntoView({ behavior: firstRender.current ? "instant" : "smooth", block: "end" });
    }, [messages]);
    useEffect(() => {
        const handleScrollToMessage = (messageId) => {
            const el = viewportRef.current?.querySelector(`[data-message-id="${messageId}"]`);
            if (!el)
                return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.style.transition = "outline 0.3s ease, outline-offset 0.3s ease";
            el.style.outline = "2px solid var(--mantine-color-blue-5)";
            el.style.borderRadius = "8px";
            setTimeout(() => {
                el.style.outline = "none";
                el.style.outlineOffset = "0px";
            }, 2000);
        };
        on("scrollToMessage", handleScrollToMessage);
        return () => {
            off("scrollToMessage", handleScrollToMessage);
        };
    }, [on, off]);
    useResizeObserver(inputWrapperRef, ({ height }) => {
        setHeight(height);
    });
    const headerHeight = 56;
    const height = `calc(100% - ${inputHeight}px - ${headerHeight}px)`;
    const hasMessages = messages.length > 0;
    return (_jsxs(Stack, { h: "100%", children: [_jsx(ChatMessagesHeader, {}), connectionState === "connecting" && (_jsx(Alert, { color: "yellow", variant: "light", py: 6, px: "md", children: _jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(Loader, { size: "xs", color: "yellow", type: "dots" }), _jsx(Text, { size: "xs", children: initialized
                                ? intl.formatMessage({ id: "chat.reconnecting", defaultMessage: "Reconnecting..." })
                                : intl.formatMessage({ id: "chat.connecting", defaultMessage: "Connecting..." }) })] }) })), connectionState === "error" && (_jsx(Alert, { color: "red", variant: "light", py: 6, px: "md", children: _jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(Loader, { size: "xs", color: "red", type: "dots" }), _jsx(Text, { size: "xs", children: intl.formatMessage({ id: "chat.error", defaultMessage: "Error connecting to chat, retrying..." }) })] }) })), hasMessages && (_jsx(ScrollArea, { h: height, w: "100%", type: "auto", viewportRef: viewportRef, offsetScrollbars: true, children: messages.map((message) => {
                    const { role, content, id } = message;
                    if (role === "system") {
                        return (_jsx(SystemMessage, { code: message.code, content: content, messageId: id }, id));
                    }
                    else if (role === "assistant") {
                        return (_jsx(AssistantBlock, { content: content, messageId: id }, id));
                    }
                    else {
                        return (_jsx(UserMessage, { content: content, attachments: message.attachments, messageId: id }, id));
                    }
                }) })), !hasMessages && _jsx(ChatEmptyState, { inputRef: inputRef })] }));
});
